import React from 'react';
import {makeRequest} from '../../utils/httpRequest';
import Config from "../../core/config";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import Cookies from "js-cookie";
import GenericAsyncSelect from "../common/AsyncSelect";
import CustomerOptionComponent from "./CustomerOptionComponent";

const apiUrl = Config.apiUrl;

/**
 * CustomerSelectAsync Component
 * 
 * An asynchronous customer selection component with search functionality.
 * 
 * @param {Object} props
 * @param {Function} props.onItemClick - Callback when a customer is selected
 * @param {Function} props.onClose - Callback when the component is closed
 * @param {string} props.placeholder - Placeholder text for the select
 * @param {Object} props.value - Current selected value (for controlled component)
 * @param {Object} props.prefilledCustomer - Customer object to prefill the select (for editing existing data)
 * @param {Object} props.style - Style object to apply to the select component (width for control, menuWidth for dropdown menu)
 * 
 * @example
 * // Basic usage
 * <CustomerSelectAsync onItemClick={handleCustomerSelect} />
 * 
 * @example
 * // With prefilled customer data (for editing)
 * <CustomerSelectAsync 
 *   onItemClick={handleCustomerSelect}
 *   prefilledCustomer={{id: 1, code: "C001", name: "John Doe"}}
 *   style={{width: "200px", menuWidth: "300px"}}
 * />
 */
const CustomerSelectAsync = (props) => {
    const {onItemClick, onClose, placeholder = "Selecione um cliente...", value = null, prefilledCustomer = null, style = {}, dataTest = "customer-async-select"} = props;
    const [, setLoading] = useRecoilState(loadingAtom);

    // Load options function for AsyncSelect
    const loadOptions = async (inputValue) => {
        // Only search if there's at least 3 characters
        if (!inputValue || inputValue.trim().length < 3) {
            return [];
        }
        
        try {
            setLoading(true);
            const url = apiUrl + '/api/customers/query=' + inputValue.trim();
            const response = await makeRequest(url, 'GET', {'Useruuid': Cookies.get('userUUID')});
            
            // Transform the response to match react-select format
            const options = response.map(customer => {
                // Handle cases where code might be null, undefined, or empty
                const code = customer.code && customer.code.toString().trim() !== '' ? customer.code : '';
                const label = code ? `${code} - ${customer.name}` : customer.name;
                
                return {
                    value: customer.id,
                    label: label,
                    customer: customer // Keep the full customer object for reference
                };
            });
            
            return options;
        } catch (error) {
            console.error('Error fetching customers:', error);
            return [];
        } finally {
            setLoading(false);
        }
    };

    // Create prefilled value if customer data is provided
    const getPrefilledValue = () => {
        if (prefilledCustomer) {
            // Handle cases where code might be null, undefined, or empty
            const code = prefilledCustomer.code && prefilledCustomer.code.toString().trim() !== '' ? prefilledCustomer.code : '';
            const label = code ? `${code} - ${prefilledCustomer.name}` : prefilledCustomer.name;
            
            return {
                value: prefilledCustomer.id,
                label: label,
                customer: prefilledCustomer
            };
        }
        return value;
    };

    return (
        <GenericAsyncSelect
            loadOptions={loadOptions}
            onItemClick={onItemClick}
            onClose={onClose}
            placeholder={placeholder}
            value={getPrefilledValue()}
            dataTest={dataTest}
            customOptionComponent={CustomerOptionComponent}
            style={style}
        />
    );
};

export default CustomerSelectAsync; 